import { Button } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import './RegionsOverviewIndexPage.less';
import {
	fetchRegionsWithChildrenForBuyer,
	fetchRegionsWithChildrenForSupplier,
	regionsRestCrudThunksForBuyer,
	regionsRestCrudThunksForSupplier,
} from '../../thunks/regions_thunks';
import { connect } from 'react-redux';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import OWSimpleHierarchyEdit from '../common/OWSimpleHierarchyEdit';
import { withUserType } from '../withUserType';
import AddHierarchyEntityPopup from '../common/AddHierarchyEntityPopup';

const REGIONS_WITH_CHILDREN = 'regionsWithChildrenDropdown';

const RegionsOverviewIndexPage: FC<any> = ({
	loading,
	regions,
	fetchRegionsWithHierarchy,
	getLocationsCount,
	userType,
	currentUser,
	companyConfig,
	createRegion,
	updateRegion,
}): React.ReactElement => {
	const [currentParent, setCurrentParent] = useState(null);
	const [addRegionVisible, setAddRegionVisible] = useState(false);

	const refreshRegions = useCallback(
		() => fetchRegionsWithHierarchy({}, REGIONS_WITH_CHILDREN),
		[fetchRegionsWithHierarchy]
	);

	useEffect(() => {
		refreshRegions();
	}, [refreshRegions]);

	const regionsHierarchy = useMemo(
		() => getRecordsForTargetCollection(regions, REGIONS_WITH_CHILDREN),
		[regions]
	);

	const showAddRootLevelRegion = useCallback(() => {
		setCurrentParent(null);
		setAddRegionVisible(true);
	}, []);

	const hideAddRegion = useCallback(() => setAddRegionVisible(false), []);

	const onRegionAddSuccess = useCallback(() => {
		hideAddRegion();
		refreshRegions();
	}, [hideAddRegion, refreshRegions]);

	return loading ? (
		<PageLoadingPlaceholder />
	) : (
		<div id="regions-tree-view" className="m-4 mt-6 flex flex-col">
			<div className="mb-4 flex flex-row justify-end">
				<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
					<Button type="primary" onClick={showAddRootLevelRegion}>
						Add Region
					</Button>
				</AccessPermissionChecker>
			</div>
			<OWSimpleHierarchyEdit
				sourceData={regionsHierarchy}
				allowedToEdit={isUserAllowedToAccess(userType)(
					PERMISSION_NAMES.MODIFY_LOCATIONS,
					companyConfig,
					nullSafeGet('roles', currentUser)
				)}
				entityName={'region'}
				createEntity={createRegion}
				updateEntity={updateRegion}
				deleteEntity={() => {}}
				getChildrenCount={getLocationsCount}
				refreshEntities={refreshRegions}
				canEditTitle={(_) => true}
				canDelete={(_) => false}
			/>
			{addRegionVisible && (
				<AddHierarchyEntityPopup
					parentData={currentParent}
					entityName={'region'}
					onSuccess={onRegionAddSuccess}
					onCancel={hideAddRegion}
					onSave={createRegion}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	regions: state.regions,
	loading: state.regions.fetching,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchRegionsWithHierarchy: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchRegionsWithChildrenForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: fetchRegionsWithChildrenForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	getLocationsCount: (regionId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.countBy({ regionId }, '')
				: locationsRestCrudThunksForBuyer.countBy({ regionId }, '')
		),
	createRegion: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? regionsRestCrudThunksForSupplier.create(entity)
				: regionsRestCrudThunksForBuyer.create(entity)
		),
	updateRegion: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? regionsRestCrudThunksForSupplier.update(entity)
				: regionsRestCrudThunksForBuyer.update(entity)
		),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(RegionsOverviewIndexPage as any)) as any
);
